/**
 * @class constructs an element held in an object.
 * @version 1.6
 */
class Element
{
    /**
     * @constructor
     * @param {object} - object of element properties:
     * - classes: array of classes or string of single class/multiple classes seperated by spaces.
     * - content: string or node to go in the element
     * - id: string for ID
     * - element: string for type of element e.g 'div', 'ul', 'li'.
     * - attributions: object (or array of objects) containing props 'attribute' and 'value' (optional).
     */
    constructor({classes = null, content = null, id = null, element = 'div', attributions = null} = {})
    {
        this.element = document.createElement(element);
        if (classes)
        {
            this.addClassToElement(classes);
        }
        if (content)
        {
            this.updateContentInElement(content)
        }
        if (id)
        {
            this.addOrUpdateIDToElement(id)
        }
        if (attributions)
        {
            Array.isArray(attributions) ?
                this.addOrUpdateMultipleAttributesToElement(attributions)
                :
                typeof attributions === 'object'
                    ?
                    this.addOrUpdateAttributeToElement(attributions.attribute, attributions.value)
                    :
                    this.throwError('Element constructor: attributions must be an object or an array of objects. {attribute, value}');
        }
        this.element.hidden = false;

        return this;
    }

    /**
     * @method chuck localised errors if the param doesn't fit the method, and then return it as the right type of variable.
     * @param param - the param to be checked. REQUIRED.
     * @param {string} paramName - the name of the param to be echoed in any error's that are thrown. REQUIRED.
     * @param {string} methodName - the name of the method to be echoed in any error's that are thrown. REQUIRED.
     * @param {string / array} type - acceptable type(s) for the param. REQUIRED.
     * @param {string} props - If the param is an object that requires a certain prop, pass it here to check it has it.
     * OPTIONAL - only to be used if the above it 'object'.
     */
    checkRequiredParamAndReturnFormatted(param, paramName, methodName, type, props)
    {
        // check the params passed to this method are formatted correctly to do the check
        paramName = paramName ? paramName : '[an undefined parameter]';
        methodName = methodName ? methodName : '[an undefined method]';
        if (typeof paramName !== 'string')
        {
            this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'paramName' argument is not a string`)
        }
        else if (typeof methodName !== 'string')
        {
            this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'methodName' argument is not a string`)
        }

        let listOfTypes;
        listOfTypes = '';
        if (type)
        {
            if (!Array.isArray(type))
            {
                if (typeof type === 'string')
                {
                    type = type.split(' ');
                }
                else
                {
                    this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'type' argument is not an array or a string`)
                }
            }

            const arrayOfValidTypes = ['symbol', 'null', 'object', 'number', 'string', 'boolean', 'array'];
            for (const t of type)
            {
                listOfTypes += (listOfTypes === '') ? t : ` or ${t}`;
                if (arrayOfValidTypes.indexOf(t) === -1)
                {
                    this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: invalid type '${t}' listed in the 'type' argument`)
                }
            }
        }


        if (props)
        {
            if (type.length > 1)
            {
                this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'props' argument passed when the 'type' argument has multiple values (can only check on one type, which is an object)`)
            }
            else if (type[0] !== 'object')
            {
                this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'props' argument passed when the 'type' argument is not an object`)
            }
            else if (!Array.isArray(props))
            {
                if (typeof props === 'string')
                {
                    props = props.split(' ');
                }
                else
                {
                    this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'props' argument is not an array or a string`)
                }
            }
        }


        // check the params
        if (param)
        {
            if (type.indexOf(typeof param) === -1)
            {
                if ((type.indexOf('array') !== -1))
                {
                    if (!Array.isArray(param))
                    {
                        this.throwError(`CreateElement - ${methodName} method: '${paramName}' argument is not a ${listOfTypes}`);
                    }
                }
                else
                {
                    this.throwError(`CreateElement - ${methodName} method: '${paramName}' argument is not a ${listOfTypes}`);
                }
            }
            else if (props)
            {
                for (const p of props)
                {
                    if (!param[p])
                    {
                        this.throwError(`CreateElement - ${methodName} method: '${p}' prop not found in '${paramName}' argument`);
                    }
                }
            }
        }
        else
        {
            this.throwError(`CreateElement - ${methodName} method: '${paramName}' argument not passed`)
        }

        //if allowed to be an array or a string, return as an array
        if ((type.indexOf('array') !== -1) && (type.indexOf('string') !== -1))
        {
            if (typeof param === 'string')
            {
                param = param.split(' ');
            }
        }
        //if allowed to be an object or string...
        else if ((type.indexOf('object') !== -1) && (type.indexOf('string') !== -1))
        {
            if (typeof param === 'string')
            {
                //... return as text node if it is a string
                param = document.createTextNode(param)
            }
            else
            {
                //... if another instantiation of Element class, return the element, else return param as is.
                param = param.element ? param.element : param;
            }
        }

        return param;
    }

    /**
     * @method throw error
     * @param {string} message - error message
     */
    throwError(message)
    {
        throw new Error(message);
    }

    // ************************************</editor-fold>
    // ADD/REMOVE ATTRIBUTE
    // ************************************<editor-fold>

    /**
     * @method add (or update) attribute to element
     * @param {string} attribute - attribute name. REQUIRED.
     * @param {string} value - attribute value. OPTIONAL.
     */
    addOrUpdateAttributeToElement(attribute, value)
    {
        this.checkRequiredParamAndReturnFormatted(attribute, 'attribute', 'addOrUpdateAttributeToElement', 'string');

        if (attribute === 'class')
        {
            value ? this.addClassToElement(value) : this.throwError('Element.addOrUpdateAttributeToElement: attribute is set to class but no value for the class is defined, which is required');
        }
        else if (attribute.indexOf(" ") === -1)
        {
            value ? this.element.setAttribute(attribute, value) : this.element.setAttribute(attribute, '');
        } else {
            console.warn("Element.addOrUpdateAttributeToElement: attribute has spaces in the string which are not allowed. To add multiple attribute use the addOrUpdateMultipleAttributesToElement method");
        }

        return this;
    }


    /**
     * @method add multiple attributes to element
     * @param {array} attributions - array of objects containing attribute and value (optional) props as strings
     */
    addOrUpdateMultipleAttributesToElement(attributions)
    {
        this.checkRequiredParamAndReturnFormatted(attributions, 'attributions', 'addOrUpdateMultipleAttributesToElement', 'object array');

        if (Array.isArray(attributions))
        {
            let i;
            for (i = 0; i < attributions.length; i++)
            {
                this.checkRequiredParamAndReturnFormatted(attributions[i], `attributions[${i}]`, 'addOrUpdateMultipleAttributesToElement', 'object', 'attribute');

                this.addOrUpdateAttributeToElement(attributions[i].attribute, attributions[i].value);
            }
        }
        else
        {
            this.checkRequiredParamAndReturnFormatted(attributions, 'attributions', 'addOrUpdateMultipleAttributesToElement', 'object', 'attribute');

            this.addOrUpdateAttributeToElement(attributions.attribute, attributions.value);
        }

        return this;
    }

    /**
     * @method remove attribute(s) from element
     * @param {string / array} attributes - attributes name. REQUIRED.
     */
    removeAttributesFromElement(attributes)
    {
        this.checkRequiredParamAndReturnFormatted(attributes, 'attributes', 'removeAttributeFromElement', 'string array');

        if (typeof attributes === 'string')
        {
            attributes = attributes.split(' ');
        }

        for (const a of attributes)
        {
            if (a !== '')
            {
                this.element.removeAttribute(a);
            }
        }

        return this;
    }

    // ************************************</editor-fold>
    // ADD/REMOVE CLASSES
    // ************************************<editor-fold>

    /**
     * @method Add class(es) to the element
     * @param {string / array} classes - an array of classes to add to the element
     * - also accepts a single class or a list of classes as a string
     * (each class to be seperated by a space)
     */
    addClassToElement(classes)
    {
        classes = this.checkRequiredParamAndReturnFormatted(classes, 'classes', 'addClassToElement', 'string array');

        for (const c of classes)
        {
            if (c !== '')
            {
                this.element.classList.add(c);
            }
        }

        return this;
    }

    /**
     * @method check if element has class
     * @param {array / string} classes - class(es) to check the element has. REQUIRED.
     * @returns {boolean} - if element has class (true) or not (false)
     */
    elementHasClass(classes)
    {
        classes = this.checkRequiredParamAndReturnFormatted(classes, 'classes', 'elementHasClass', 'string array');

        let boolean;

        if (typeof classes === 'string')
        {
            classes = classes.split(' ');
        }

        for (const c of classes)
        {

            if (c !== '')
            {
                boolean = this.element.classList.contains(c);
            }
        }

        return boolean;
    }

    /**
     * @method Remove class(es) from the element
     * @param {string / array} classes - an array of classes to remove from the element.  OPTIONAL - will remove all classes if not passed.
     * - also accepts a single class or a list of classes as a string (each class to be separated by a space)
     * @param {boolean} invert - invert the classes, to remove all but those classes.
     */
    removeClassFromElement(classes, invert)
    {
        if (!classes || invert)
        {
            this.element.className = '';
        }

        if (classes)
        {
            if (typeof classes === 'string')
            {
                classes = classes.split(' ');
            }

            for (const c of classes)
            {
                if (c !== '')
                {
                    invert ? this.element.classList.add(c) : this.element.classList.remove(c);
                }
            }
        }

        return this;
    }

    /**
     * @method add and classesToRemove a class simultaneously
     * @param {string / array} classesToAdd  - class(es) to add. REQUIRED.
     * @param {string / array} classesToRemove  - class(es) to remove. REQUIRED.
     */
    addAndRemoveClassToElement(classesToAdd, classesToRemove)
    {
        this.checkRequiredParamAndReturnFormatted(classesToAdd, 'classesToAdd', 'addAndRemoveClassToElement', 'string array');
        this.checkRequiredParamAndReturnFormatted(classesToRemove, 'classesToRemove', 'addAndRemoveClassToElement', 'string array');

        this.addClassToElement(classesToAdd);
        this.removeClassFromElement(classesToRemove, false);

        return this;
    }

    /**
     * @method Remove class(es) to the element if they exist, add them if they do not.
     * @param {array / string} classes - an array of classes to add/remove. REQUIRED.
     * - also accepts a single class or a list of classes as a string (each class to be seperated by a space)
     */
    toggleClassOnElement(classes)
    {
        classes = this.checkRequiredParamAndReturnFormatted(classes, 'classes', 'toggleClassOnElement', 'string array');

        for (const c of classes)
        {
            if (c !== '')
            {
                this.elementHasClass(c)
                    ?
                    this.element.classList.remove(c)
                    :
                    this.element.classList.add(c);
            }
        }

        return this;
    }


    // ************************************</editor-fold>
    // ADD/REMOVE ID
    // ************************************<editor-fold>

    /**
     * @method add ID to Element
     * @param {string} id - ID to add to element. REQUIRED.
     */
    addOrUpdateIDToElement(id)
    {
        this.checkRequiredParamAndReturnFormatted(id, 'id', 'addOrUpdateIDToElement', 'string');

        this.element.setAttribute('id', id);

        return this;
    }

    /**
     * @method remove ID
     */
    removeIDFromElement()
    {
        this.element.setAttribute('id', '');

        return this;
    }


    // ************************************</editor-fold>
    // ADD/REMOVE STYLES
    // ************************************<editor-fold>

    /**
     * @method add styleS to the element
     * @param {array} arrayOfStyles - array of objects with properties 'property' and 'style'
     */
    addOrUpdateMultipleStylesToElement(arrayOfStyles)
    {
        this.checkRequiredParamAndReturnFormatted(arrayOfStyles, 'arrayOfStyles', 'addOrUpdateMultipleStylesToElement', 'array');

        for (const style of arrayOfStyles)
        {
            this.checkRequiredParamAndReturnFormatted(style, 'style in arrayOfStyles', 'addOrUpdateMultipleStylesToElement', 'object', 'property style');

            this.addOrUpdateStyleToElement(style.property, style.style);
        }

        return this;
    }

    /**
     * @method add style to the element
     * @param {string} property - style property. REQUIRED.
     * @param {string} value - style value. REQUIRED.
     */
    addOrUpdateStyleToElement(property, value)
    {
        this.checkRequiredParamAndReturnFormatted(property, 'property', 'addOrUpdateStyleToElement', 'string');
        this.checkRequiredParamAndReturnFormatted(value, 'value', 'addOrUpdateStyleToElement', 'string');

        this.element.style[property] = value;

        return this;
    }

    /**
     * @method remove style property/properties from element
     * @param {string / array} properties - propertie to remove: a single property name, list of properties (seperated by a space " "), or array of properties (strings). REQUIRED.
     */
    removeStylesFromElement(properties)
    {
        this.checkRequiredParamAndReturnFormatted(properties, 'properties', 'removeStylesFromElement', 'string array');

        if (typeof properties === 'string')
        {
            properties = properties.split(' ');
        }

        for (const property of properties)
        {
            if (property !== '')
            {
                this.element.style[property] = '';
            }
        }
    }


    // ************************************</editor-fold>
    // ANIMATE THE ELEMENT
    // ************************************<editor-fold>

    /**
     * @method animate the element using animate.css classes
     * @param {string} animationEffect - the class of the animationEffect to use. REQUIRED
     * @param {int} speed - speed of the animationEffect. OPTIONAL - defaults to previously set or 1000ms
     * @param {boolean} infinite - repeat the animationEffect infinitely or not. OPTIONAL - defaults to false.
     * @param {boolean} hideElementAfter - hide the element after animation complete. OPTIONAL - defaults to false.
     */
    animateTheElement(animationEffect, speed, infinite, hideElementAfter)
    {
        this.checkRequiredParamAndReturnFormatted(animationEffect, 'animationEffect', 'animateTheElement', 'string');

        if (!this.elementHasClass('animated'))
        {
            this.addClassToElement('animated')
        }

        if (speed)
        {
            this.element.classList.forEach((className) =>
            {
                if (className.startsWith('speed-'))
                {
                    this.element.classList.remove(className);
                }
            });

            speed = (Math.round(speed / 100) * 100);
            const speedClass = 'speed-' + speed;
            this.addClassToElement(speedClass);
        }

        this.addClassToElement(animationEffect);

        if (infinite)
        {
            this.addClassToElement('infinite');
        }
        else
        {
            this.removeClassFromElement('infinite');
            setTimeout(function (element)
            {
                if (hideElementAfter)
                {
                    element.hideTheElement();
                }
                element.removeClassFromElement(animationEffect);
            }, speed ? speed + 100 : 1100, this);
        }

        return this;
    }

    /**
     * @method animate the element in or out of view using animate.css classes
     * @param {string} animationEffect - the class of the animationEffect to use. REQUIRED
     * @param {int} speed - speed of the animationEffect. OPTIONAL - defaults to previously set or 1000ms
     * @param {boolean} hideElementAfter - hide element after animation. OPTIONAL.
     */
    animateTheElementInOrOut(animationEffect, speed, hideElementAfter)
    {
        this.checkRequiredParamAndReturnFormatted(animationEffect, 'animationEffect', ' animateTheElementInOrOut', 'string');

        if (!this.element.hidden || hideElementAfter)
        {
            this.animateTheElement(animationEffect, speed, false, true);
        }
        else
        {
            this.showTheElement(true);
            this.animateTheElement(animationEffect, speed, false, false);
        }

        return this;
    }


    // ************************************</editor-fold>
    // CONTENT: APPEND / UPDATE / PREPEND / REMOVE / RETRIEVE / WRAP
    // ************************************<editor-fold>

    /**
     * @method appendToElement something to the element
     * @param {element / node / string} item - thing to appendToElement.
     */
    appendToElement(item)
    {
        item = item ? item : ' ';
        item = (typeof item === 'number') ? item.toString() : item;
        item = this.checkRequiredParamAndReturnFormatted(item, 'item', 'appendToElement', 'object string');
        this.element.appendChild(item);

        return this;
    }

    /**
     * @method remove Element's previous content and then add new content
     * @param {node / string / element / int} content - content to be added. REQUIRED.
     */
    updateContentInElement(content)
    {
        // this.checkRequiredParamAndReturnFormatted(content, 'content', 'updateContentInElement', 'object string number');

        this.element.innerHTML = '';
        this.appendToElement(content);

        return this;
    }

    /**
     * @method add new HTML to implement. Useful for templating
     * @param HTML - HTML to update
     */
    updateHTMLOfElement(HTML)
    {
        this.element.innerHTML = HTML;

        return this;
    }

    /**
     * @method prependToElement - prepend something to the element
     * @param {element / node / string} item - thing to appendToElement
     */
    prependToElement(item)
    {
        item = item ? item : ' ';
        item = this.checkRequiredParamAndReturnFormatted(item, 'item', 'appendToElement', 'object string');
        this.element.firstChild ? this.element.insertBefore(item, this.element.firstChild) : this.element.appendChild(item);
        return this;
    }

    /**
     * @method remove all children nodes
     */
    removeAllChildrenFromElement()
    {
        while (this.element.firstChild)
        {
            this.element.removeChild(this.element.firstChild);
        }
    }

    /**
     * @method return the contents of the element
     * @returns {HTMLCollection} - contents of the element
     */
    retrieveContentsFromElement()
    {
        return this.element.children;
    }

    /**
     * @method take the contents of the element and wrap in another element
     * @param {object} wrapperElementOptions - element to wrap contents in:
     * takes all the props the Element constructor takes: {classes, content, id, element, attributions}.
     * OPTIONAL.
     */
    wrapContentOfElement(wrapperElementOptions)
    {
        const wrapper = new Element(wrapperElementOptions);
        [...this.returnElement().childNodes].forEach((child) => wrapper.appendToElement(child));
        this.appendToElement(wrapper.returnElement());

        return this;
    }


    // ************************************</editor-fold>
    // HIDE/SHOW ELEMENT
    // ************************************<editor-fold>

    /**
     * @method set element style="display: none"
     */
    hideTheElement()
    {
        this.element.style.display = 'none';
        this.element.hidden = true;

        return this;
    }

    /**
     * @method set element style="display: block" (or display type defined in the parameter)
     * @param {boolean} removeStyleAttribute - show the element by removing style attribute all together, rather than changing the display declaration within the style attribute. OPTIONAL.
     * @param {string} display - display declaration (accepts any display css declarations: inline, inline-block etc). OPTIONAL - defaults to block.
     */
    showTheElement(removeStyleAttribute, display = 'block')
    {
        if (removeStyleAttribute)
        {
            this.element.removeAttribute('style');
        }
        else
        {
            this.element.style.display = display;
        }
        this.element.hidden = false;

        return this;
    }

    /**
     * @method toggle the element in and out of view
     * @param {boolean} removeStyleAttributeOnShow - show the element by removing style attribute all together, rather than changing the display declaration within the style attribute. OPTIONAL.
     * @param {boolean} forceHide - force the element to be hidden. OPTIONAL.
     * @param {boolean} display - display declaration (accepts any display css declarations: inline, inline-block etc) for when shown. OPTIONAL - defaults to block.
     */
    toggleTheElement(removeStyleAttributeOnShow, forceHide, display = 'block')
    {
        if (forceHide)
        {
            this.hideTheElement();
        }
        else
        {
            this.element.hidden ? this.showTheElement(removeStyleAttributeOnShow, display) : this.hideTheElement();
        }

        return this;
    }


    // ************************************</editor-fold>
    // DUPLICATE / RETURN THE ELEMENT
    // ************************************<editor-fold>

    /**
     * @method duplicate the element
     * @param {boolean} duplicateChildrenNodes - include children nodes in the duplication
     */
    duplicateTheElement(duplicateChildrenNodes = true)
    {
        const duplicateElement = new Element();
        duplicateElement.element = this.element.cloneNode(duplicateChildrenNodes);
        return duplicateElement;
    }

    /**
     * @method return element as a node (remove from object).
     * @returns {node} - element as a node.
     */
    returnElement()
    {
        return this.element;
    }
}

// ************************************</editor-fold>
// EXPORTS - uncomment module.exports for JEST tests.
// ************************************<editor-fold>
// module.exports = {Element};
// export {Element};
